import React, { useEffect, useState } from 'react';
import { useLang } from '../../contexts/LangContext';
import {
  apartmentsApi,
  type Apartment,
  type ApartmentMember,
  type CreateApartmentMemberData,
} from '../../api/apartmentsApi';
import { formatDateToDisplay, parseDisplayDateToIso } from '../../utils/apartmentDate';
import { isValidEmailList } from '../../utils/emailList';
import {
  RESIDENT_GENDER_OPTIONS,
  RESIDENT_NATIONALITY_OPTIONS,
  RESIDENT_RELATIONSHIP_OPTIONS,
  RESIDENT_ROLE_OPTIONS,
} from '../../utils/residentLookup';

interface ResidentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  buildingId: string;
  resident?: ApartmentMember | null;
}

const emptyForm = (buildingId: string, apartmentId = ''): CreateApartmentMemberData => ({
  apartmentId,
  fullName: '',
  dateOfBirth: '',
  role: 'NHAN_KHAU',
  relationshipCode: '',
  gender: undefined,
  nationality: 'VN',
  moveInDate: '',
  moveOutDate: '',
  email: '',
  phone: '',
  citizenId: '',
  idIssueDate: '',
  idExpiryDate: '',
  notes: '',
  invoiceEmail: '',
});

const toNullableText = (value?: string | null): string | null => {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
};

const toNullableDate = (value?: string | null): string | null => {
  if (!value?.trim()) return null;
  return parseDisplayDateToIso(value) ?? null;
};

const ResidentFormModal: React.FC<ResidentFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  buildingId,
  resident,
}) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [formData, setFormData] = useState<CreateApartmentMemberData>(emptyForm(buildingId));

  useEffect(() => {
    if (!isOpen || !buildingId) return;

    apartmentsApi.getApartments({ buildingId, limit: 5000 }).then(setApartments).catch(() => setApartments([]));

    if (resident) {
      setFormData({
        apartmentId: resident.apartmentId,
        fullName: resident.fullName,
        dateOfBirth: formatDateToDisplay(resident.dateOfBirth),
        role: resident.role,
        relationshipCode: resident.relationshipCode || '',
        gender: resident.gender,
        nationality: resident.nationality || 'VN',
        moveInDate: formatDateToDisplay(resident.moveInDate),
        moveOutDate: formatDateToDisplay(resident.moveOutDate),
        email: resident.email || '',
        phone: resident.phone || '',
        citizenId: resident.citizenId || '',
        idIssueDate: formatDateToDisplay(resident.idIssueDate),
        idExpiryDate: formatDateToDisplay(resident.idExpiryDate),
        notes: resident.notes || '',
        invoiceEmail: resident.invoiceEmail || '',
      });
    } else {
      setFormData(emptyForm(buildingId, apartments[0]?.id || ''));
    }
    setError(null);
  }, [isOpen, buildingId, resident]);

  useEffect(() => {
    if (isOpen && !resident && apartments.length > 0 && !formData.apartmentId) {
      setFormData((prev) => ({ ...prev, apartmentId: apartments[0].id }));
    }
  }, [apartments, isOpen, resident, formData.apartmentId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.fullName?.trim()) {
      setError(t('pleaseEnterFullName'));
      return;
    }
    if (!formData.apartmentId) {
      setError(t('pleaseSelectApartment'));
      return;
    }
    if (!isValidEmailList(formData.email)) {
      setError(t('invalidEmailList'));
      return;
    }
    if (!isValidEmailList(formData.invoiceEmail)) {
      setError(t('invalidEmailList'));
      return;
    }

    const payload: CreateApartmentMemberData = {
      ...formData,
      fullName: formData.fullName.trim(),
    };

    if (resident) {
      Object.assign(payload, {
        dateOfBirth: toNullableDate(formData.dateOfBirth),
        moveInDate: toNullableDate(formData.moveInDate),
        moveOutDate: toNullableDate(formData.moveOutDate),
        idIssueDate: toNullableDate(formData.idIssueDate),
        idExpiryDate: toNullableDate(formData.idExpiryDate),
        relationshipCode: toNullableText(formData.relationshipCode),
        gender: formData.gender || null,
        nationality: toNullableText(formData.nationality),
        email: toNullableText(formData.email),
        phone: toNullableText(formData.phone),
        citizenId: toNullableText(formData.citizenId),
        notes: toNullableText(formData.notes),
        invoiceEmail: toNullableText(formData.invoiceEmail),
      });
    } else {
      Object.assign(payload, {
        dateOfBirth: parseDisplayDateToIso(formData.dateOfBirth),
        moveInDate: parseDisplayDateToIso(formData.moveInDate),
        moveOutDate: parseDisplayDateToIso(formData.moveOutDate),
        idIssueDate: parseDisplayDateToIso(formData.idIssueDate),
        idExpiryDate: parseDisplayDateToIso(formData.idExpiryDate),
        relationshipCode: formData.relationshipCode || undefined,
        gender: formData.gender || undefined,
        nationality: formData.nationality || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        citizenId: formData.citizenId || undefined,
        notes: formData.notes || undefined,
        invoiceEmail: formData.invoiceEmail || undefined,
      });
    }

    try {
      setLoading(true);
      if (resident) {
        await apartmentsApi.updateApartmentMember(resident.id, payload);
      } else {
        await apartmentsApi.createApartmentMember(payload);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(t('errorSavingResident'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {resident ? t('editResident') : t('addResident')}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('residentApartmentCode')} *</label>
            <select
              name="apartmentId"
              value={formData.apartmentId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">{t('selectApartment')}</option>
              {apartments.map((apt) => (
                <option key={apt.id} value={apt.id}>{apt.code}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('residentName')} *</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('residentRole')}</label>
            <select name="role" value={formData.role} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              {RESIDENT_ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('relationshipRole')}</label>
            <select name="relationshipCode" value={formData.relationshipCode || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">{t('noUsageType')}</option>
              {RESIDENT_RELATIONSHIP_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('feeNotificationEmail')}</label>
            <input type="text" name="email" value={formData.email || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            <p className="mt-1 text-xs text-gray-500">{t('multipleEmailsHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('invoiceEmail')}</label>
            <input type="text" name="invoiceEmail" value={formData.invoiceEmail || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
            <p className="mt-1 text-xs text-gray-500">{t('multipleEmailsHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('phone')}</label>
            <input type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('gender')}</label>
            <select name="gender" value={formData.gender || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">{t('noUsageType')}</option>
              {RESIDENT_GENDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('dateOfBirth')}</label>
            <input type="text" name="dateOfBirth" value={formData.dateOfBirth || ''} onChange={handleChange} placeholder={t('handoverDatePlaceholder')} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('nationality')}</label>
            <select name="nationality" value={formData.nationality || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md">
              <option value="">{t('noUsageType')}</option>
              {RESIDENT_NATIONALITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('citizenId')}</label>
            <input type="text" name="citizenId" value={formData.citizenId || ''} onChange={handleChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('idIssueDate')}</label>
            <input type="text" name="idIssueDate" value={formData.idIssueDate || ''} onChange={handleChange} placeholder={t('handoverDatePlaceholder')} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('idExpiryDate')}</label>
            <input type="text" name="idExpiryDate" value={formData.idExpiryDate || ''} onChange={handleChange} placeholder={t('handoverDatePlaceholder')} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('moveInDate')}</label>
            <input type="text" name="moveInDate" value={formData.moveInDate || ''} onChange={handleChange} placeholder={t('handoverDatePlaceholder')} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('moveOutDate')}</label>
            <input type="text" name="moveOutDate" value={formData.moveOutDate || ''} onChange={handleChange} placeholder={t('handoverDatePlaceholder')} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('apartmentNotes')}</label>
            <textarea name="notes" value={formData.notes || ''} onChange={handleChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md" />
          </div>

          <div className="md:col-span-2 flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-md">
              {t('close')}
            </button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">
              {loading ? t('saving') : t('save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResidentFormModal;
